import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LoadingSpinner } from '../../../../common';
import { AdminEmptyState } from '../../../ui';
import { useAuth } from '../../../../../contexts/AuthContext';
import { WorkspaceSettingsPage } from '../../../../workspace/pages/WorkspaceSettingsPage';
import { Workspace } from '../../../../../types/workspace';
import workspaceService from '../../../../../services/workspaceService';
import { Building2, ArrowLeft, TrendingUp, Crown, Users } from 'lucide-react';

/**
 * WorkspaceManagement - Gestão de workspaces do usuário (Modelo 1:N)
 *
 * COMPORTAMENTO:
 * - TD users (superadmin OU membros do workspace TD): Veem TODOS os workspaces
 * - Regular users: Veem apenas workspaces onde são membros
 * - Backend controla automaticamente quais workspaces cada usuário pode ver
 * - Badges indicam role: "Owner", "Member", "Gestão TD"
 * - Permissões controlam o que pode fazer em cada workspace (gerenciar/visualizar)
 */
export const WorkspaceManagement: React.FC = () => {
  const { user } = useAuth();

  // Workspaces list
  const [allWorkspaces, setAllWorkspaces] = useState<Workspace[]>([]);
  const [isLoadingAll, setIsLoadingAll] = useState(true);
  const [errorAll, setErrorAll] = useState<string | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [reloadTrigger, setReloadTrigger] = useState(0); // Trigger for manual reload

  // Determine if user is TD (superadmin)
  const isTDUser = user?.isSuperAdmin || false;

  // Load workspaces when component mounts or when user changes (isTDUser)
  // IMPORTANT: Must depend on isTDUser to reload when AuthContext populates user
  useEffect(() => {
    // Only load if user is loaded (prevents loading with isTDUser=false before user loads)
    if (!user) return;

    // Cancellation flag to prevent race conditions
    let cancelled = false;

    const loadData = async () => {
      setIsLoadingAll(true);
      setErrorAll(null);

      try {
        // IMPORTANT: Always use getAllWorkspaces() - backend handles permissions
        // TD users (superadmin OR TD workspace members): Get ALL workspaces
        // Regular users: Get only workspaces where they are members
        // The backend endpoint already has this logic built-in
        const response = await workspaceService.getAllWorkspaces();

        // Only update state if not cancelled (prevents race condition)
        if (!cancelled) {
          console.log('WorkspaceManagement: API response', { isTDUser, response });

          if (response.success && response.data) {
            // Handle different response formats
            let workspaces: Workspace[] = [];

            if (Array.isArray(response.data)) {
              workspaces = response.data;
            } else if (response.data.workspaces && Array.isArray(response.data.workspaces)) {
              workspaces = response.data.workspaces;
            }

            console.log('WorkspaceManagement: Parsed workspaces', workspaces);
            setAllWorkspaces(workspaces);
          }
        }
      } catch (err) {
        // Only update error state if not cancelled
        if (!cancelled) {
          console.error('WorkspaceManagement: Error loading workspaces', err);

          // User-friendly error messages
          let userMessage = 'Não foi possível carregar os workspaces. ';

          if (axios.isAxiosError(err)) {
            if (err.response?.status === 403) {
              userMessage += 'Você não tem permissão para acessar esta área.';
            } else if (err.response?.status === 401) {
              userMessage += 'Sua sessão expirou. Por favor, faça login novamente.';
            } else if (err.response?.status && err.response.status >= 500) {
              userMessage += 'Erro no servidor. Tente novamente em alguns instantes.';
            } else if (err.code === 'ERR_NETWORK') {
              userMessage += 'Verifique sua conexão com a internet.';
            } else {
              userMessage += 'Tente novamente mais tarde.';
            }
          } else {
            userMessage += 'Erro inesperado. Tente novamente.';
          }

          setErrorAll(userMessage);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAll(false);
        }
      }
    };

    loadData();

    // Cleanup: cancel request if component unmounts or deps change
    return () => {
      cancelled = true;
    };
  }, [user?.id, isTDUser, reloadTrigger]); // Reload when user, isTDUser or reloadTrigger changes

  // Manual reload function (triggers useEffect)
  const loadAllWorkspaces = () => {
    setReloadTrigger(prev => prev + 1);
  };

  const handleSelectWorkspace = (workspace: Workspace) => {
    setSelectedWorkspace(workspace);
  };

  const handleBackToList = () => {
    setSelectedWorkspace(null);
    loadAllWorkspaces(); // Reload list to show updated data
  };

  // Loading state
  if (isLoadingAll) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  // If a workspace is selected, show full management view
  if (selectedWorkspace) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Back Button */}
        <button
          onClick={handleBackToList}
          className="flex items-center gap-2 mb-6 px-4 py-2 bg-[#2a2a2a] hover:bg-[#333333] text-white rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar para lista de workspaces</span>
        </button>

        {/* Full workspace management */}
        <WorkspaceSettingsPage preSelectedWorkspace={selectedWorkspace} />
      </div>
    );
  }

  // Error state
  if (errorAll) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
          {errorAll}
        </div>
      </div>
    );
  }

  // No workspaces state
  if (allWorkspaces.length === 0) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
        <AdminEmptyState
          title="Nenhum workspace encontrado"
          description={
            isTDUser
              ? "Não há workspaces cadastrados no sistema"
              : "Você ainda não faz parte de nenhum workspace. Aguarde um convite ou entre em contato com o administrador."
          }
        />
      </div>
    );
  }

  // Render list of workspaces
  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {isTDUser ? 'Gestão de Workspaces (Tributo Devido)' : 'Meus Workspaces'}
        </h1>
        <p className="text-[#888888]">
          {isTDUser
            ? `Gerenciar todos os workspaces do sistema • ${allWorkspaces.length} workspace${allWorkspaces.length !== 1 ? 's' : ''}`
            : `Você faz parte de ${allWorkspaces.length} workspace${allWorkspaces.length !== 1 ? 's' : ''}`
          }
        </p>
      </div>

      {/* Workspaces Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {allWorkspaces.map((workspace) => {
          const tokenUsagePercentage = (workspace.token_used / workspace.monthly_token_limit) * 100;

          return (
            <button
              key={workspace.id}
              onClick={() => handleSelectWorkspace(workspace)}
              className="bg-[#2a2a2a] border border-[#333333] rounded-xl p-6 hover:border-[#F4CC04] transition-colors text-left w-full"
            >
              {/* Workspace Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#F4CC04]/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#F4CC04]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      {workspace.name}
                    </h3>
                    <p className="text-xs text-[#888888]">ID: {workspace.id}</p>
                  </div>
                </div>
              </div>

              {/* Token Usage */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#888888] flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    Uso de Tokens
                  </span>
                  <span className="font-medium text-white">
                    {tokenUsagePercentage.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-[#1a1a1a] rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      tokenUsagePercentage >= 90
                        ? 'bg-red-500'
                        : tokenUsagePercentage >= 70
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(tokenUsagePercentage, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-[#888888] mt-1">
                  {workspace.token_used.toLocaleString()} / {workspace.monthly_token_limit.toLocaleString()} tokens
                </p>
              </div>

              {/* Workspace Info */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#888888]">Owner ID:</span>
                  <span className="text-white font-medium">{workspace.owner_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#888888]">Criado em:</span>
                  <span className="text-white">
                    {new Date(workspace.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>

              {/* Badges */}
              <div className="mt-4 pt-4 border-t border-[#333333] flex flex-wrap gap-2">
                {/* Owner/Member/Gestão Badge */}
                {workspace.is_owner ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-500/20 text-amber-400">
                    <Crown className="w-3 h-3" />
                    Owner
                  </span>
                ) : isTDUser ? (
                  // TD user managing workspace (not owner/member)
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
                    <Building2 className="w-3 h-3" />
                    Gestão TD
                  </span>
                ) : (
                  // Regular user as member
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                    <Users className="w-3 h-3" />
                    Member
                  </span>
                )}

                {/* TD Workspace Badge */}
                {workspace.is_td_workspace && (
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400">
                    Workspace TD
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WorkspaceManagement;
